/**
 * Arbitrage Opportunity Calculator
 *
 * Core business logic for identifying profitable car arbitrage opportunities
 * between European markets.
 */

import { normalizeFuel } from '../utils/normalization.js';

/**
 * Helper function to get first image from images JSON
 */
function getFirstImage(imagesJson) {
  if (!imagesJson) return null;
  if (typeof imagesJson === 'string') {
    try {
      const parsed = JSON.parse(imagesJson);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : null;
    } catch (e) {
      return null;
    }
  }
  return Array.isArray(imagesJson) && imagesJson.length > 0 ? imagesJson[0] : null;
}

/**
 * Find similar cars in target market for a given source car.
 * Uses canonical_model_id for strict model matching (320↔320, not 320↔M340).
 * Falls back to family matching only when canonical_model_id is unavailable.
 * Two-pass approach: strict (with version) then relaxed (without version).
 */
function findSimilarCars(sourceCar, targetCars, matchingCriteria = {}) {
  const sourceMakeId = sourceCar.canonical_make_id;
  const sourceModelId = sourceCar.canonical_model_id;
  const sourceFamily = sourceCar.family;
  const sourceFuel = normalizeFuel(sourceCar.fuel_type);
  const sourceVersion = sourceCar.normalized_version || 'standard';
  const { yearTolerance = 1, mileageTolerance = 10000 } = matchingCriteria;

  // Need at least make + family to match
  if (!sourceFamily || !sourceMakeId) return [];

  // Base criteria: make (by ID) + model/family + fuel + year + mileage (user-configurable tolerances)
  // Exclude the source car itself (important for same-country comparisons)
  const baseSimilar = targetCars.filter(targetCar => {
    if (targetCar.id === sourceCar.id) return false;
    const makeSame = targetCar.canonical_make_id === sourceMakeId;
    // Strict model match when both have canonical_model_id, otherwise fall back to family
    const modelSame = (sourceModelId && targetCar.canonical_model_id)
      ? targetCar.canonical_model_id === sourceModelId
      : targetCar.family === sourceFamily;
    const fuelSame = normalizeFuel(targetCar.fuel_type) === sourceFuel;
    const yearClose = Math.abs((targetCar.year || 0) - (sourceCar.year || 0)) <= yearTolerance;
    const mileageClose = Math.abs((targetCar.mileage || 0) - (sourceCar.mileage || 0)) <= mileageTolerance;
    return makeSame && modelSame && fuelSame && yearClose && mileageClose;
  });

  if (baseSimilar.length === 0) return [];

  // Try strict match first (with version)
  const strictMatch = baseSimilar.filter(targetCar =>
    (targetCar.normalized_version || 'standard') === sourceVersion
  );

  // Use strict if we got matches, otherwise use base (all versions)
  return strictMatch.length > 0 ? strictMatch : baseSimilar;
}

/**
 * Build an opportunity object from source car and similar target cars
 */
function buildOpportunity(sourceCar, similarInTarget, targetCountry, calculateTransportCost, calculateImportTax) {
  // Use median price — more robust than mean (not skewed by outliers)
  const prices = similarInTarget.map(car => car.price || 0).sort((a, b) => a - b);
  const mid = Math.floor(prices.length / 2);
  const medianTargetPrice = prices.length % 2 === 0
    ? (prices[mid - 1] + prices[mid]) / 2
    : prices[mid];
  const sameCountry = sourceCar.source_country === targetCountry;
  const transportCost = sameCountry ? 0 : calculateTransportCost(sourceCar.source_country, targetCountry);
  const importTax = sameCountry ? 0 : calculateImportTax(sourceCar, targetCountry);
  const registrationFee = sameCountry ? 0 : 350;
  const sourcePrice = parseFloat(sourceCar.price) || 0;
  const totalCost = sourcePrice + transportCost + importTax + registrationFee;
  const netProfit = medianTargetPrice - totalCost;
  const profitMargin = medianTargetPrice > 0 ? (netProfit / medianTargetPrice) * 100 : 0;

  if (netProfit <= 1000) return null;

  return {
    id: sourceCar.id,
    make: sourceCar.make,
    model: sourceCar.model,
    version: sourceCar.version,
    normalizedVersion: sourceCar.normalized_version || 'standard',
    year: sourceCar.year,
    fuelType: normalizeFuel(sourceCar.fuel_type),
    image: getFirstImage(sourceCar.images) || null,
    sourceCountry: sourceCar.source_country,
    targetCountry: targetCountry,
    sourcePrice: sourcePrice,
    sourceCurrency: sourceCar.currency || '€',
    transportCost: transportCost,
    importTax: importTax,
    registrationFee: registrationFee,
    expectedSalePrice: Math.round(medianTargetPrice),
    netProfit: Math.round(netProfit),
    profitMargin: profitMargin.toFixed(1),
    daysToSell: 20,
    demandScore: 85,
    riskLevel: profitMargin > 15 ? 'low' : 'medium',
    sourceUrl: sourceCar.source_url,
    mileage: sourceCar.mileage || 0,
    condition: sourceCar.condition || 'Good',
    similarCarsInTarget: similarInTarget.length,
    co2: sourceCar.co2_emissions_wltp || sourceCar.co2_emissions_nedc || null,
    engineCC: sourceCar.engine_cc || null,
    qualityFlags: sourceCar.quality_flags || null,
    qualitySummary: sourceCar.quality_summary || null,
    qualitySummaryFr: sourceCar.quality_summary_fr || null,
    qualitySummaryPt: sourceCar.quality_summary_pt || null,
    qualitySummaryEs: sourceCar.quality_summary_es || null,
    qualityScore: sourceCar.quality_score || null
  };
}

/**
 * Calculate arbitrage opportunities from a specific source country to target country
 */
export function calculateArbitrageOpportunities(
  listings,
  sourceCountry,
  targetCountry,
  calculateTransportCost,
  calculateImportTax,
  matchingCriteria = {}
) {
  if (sourceCountry === 'all') {
    return calculateMultiSourceArbitrage(listings, targetCountry, calculateTransportCost, calculateImportTax, matchingCriteria);
  }

  const sourceCars = listings.filter(car => car.source_country === sourceCountry);
  const targetCars = listings.filter(car => car.source_country === targetCountry);
  const opportunities = [];

  console.log(`🔍 Arbitrage: ${sourceCountry} → ${targetCountry} (${sourceCars.length} source, ${targetCars.length} target)`);

  sourceCars.forEach(sourceCar => {
    const similarInTarget = findSimilarCars(sourceCar, targetCars, matchingCriteria);
    if (similarInTarget.length === 0) return;

    const opp = buildOpportunity(sourceCar, similarInTarget, targetCountry, calculateTransportCost, calculateImportTax);
    if (opp) opportunities.push(opp);
  });

  console.log(`📊 Found ${opportunities.length} profitable opportunities`);
  return opportunities.sort((a, b) => b.netProfit - a.netProfit);
}

/**
 * Calculate arbitrage opportunities from all source countries to target country
 */
export function calculateMultiSourceArbitrage(
  listings,
  targetCountry,
  calculateTransportCost,
  calculateImportTax,
  matchingCriteria = {}
) {
  const targetCars = listings.filter(car => car.source_country === targetCountry);
  const sourceCars = listings.filter(car => car.source_country !== targetCountry);
  const opportunities = [];

  console.log(`🔍 Multi-source arbitrage: ALL → ${targetCountry} (${sourceCars.length} source, ${targetCars.length} target)`);

  sourceCars.forEach(sourceCar => {
    const similarInTarget = findSimilarCars(sourceCar, targetCars, matchingCriteria);
    if (similarInTarget.length === 0) return;

    const opp = buildOpportunity(sourceCar, similarInTarget, targetCountry, calculateTransportCost, calculateImportTax);
    if (opp) opportunities.push(opp);
  });

  console.log(`📊 Found ${opportunities.length} profitable opportunities`);
  return opportunities.sort((a, b) => b.netProfit - a.netProfit);
}
